import type { TFunction } from "i18next";

export type OrganicDefinitionInfo = {
  description: string;
  formula?: string;
};

const CONTENT_KPI_KEY: Record<string, string> = {
  "posts created": "postsCreated",
  "total posts created": "postsCreated",
  "organic reach": "organicReach",
  ctr: "ctr",
  "engagement rate": "engagementRate",
  engagement: "engagementRate",
  viewers: "viewers",
};

export type OrganicContentSectionKey =
  | "totalEngagements"
  | "contentTypeBreakdown"
  | "engagementRateByType"
  | "contentPerformanceByType"
  | "trendsLast30Days"
  | "postLevelPerformance";

const FB_FOLLOWERS_KPI_KEY: Record<string, string> = {
  "total followers": "totalFollowers",
  "avg. new followers": "avgNewFollowers",
  "total followers by country": "followersByCountry",
  "total followers by city": "followersByCity",
};

const IG_FOLLOWERS_KPI_KEY: Record<string, string> = {
  ...FB_FOLLOWERS_KPI_KEY,
  "gender demographics": "genderDemographics",
};

function resolveMapKey(
  rawTitle: string,
  rawTooltip: string | undefined,
  keyMap: Record<string, string>
): string | undefined {
  const candidates = [rawTooltip, rawTitle].filter(
    (s): s is string => typeof s === "string" && s.length > 0
  );
  for (const raw of candidates) {
    const mapped = keyMap[raw.trim().toLowerCase()];
    if (mapped) {
      return mapped;
    }
  }
  return undefined;
}

function readHelpBundle(
  t: TFunction,
  basePath: string,
  leafKey: string
): OrganicDefinitionInfo {
  const description = t(`${basePath}.${leafKey}.description`);
  const formula = t(`${basePath}.${leafKey}.formula`, { defaultValue: "" });
  return formula ? { description, formula } : { description };
}

export type OrganicPlatform = "facebook" | "instagram" | "tiktok" | "snapchat";

// Metric leaves whose tooltip copy is platform-specific (the wording calls out
// "the Facebook post" / "the Instagram post" explicitly). These read from
// organicMedia.<platform>.kpiHelp instead of the shared
// organicMedia.contentReport.kpiHelp bundle both reports fall back to.
const PLATFORM_KPI_HELP_LEAVES: Record<OrganicPlatform, Set<string>> = {
  facebook: new Set([
    "impressions",
    "linkClicks",
    "likes",
    "comments",
    "engagementRate",
  ]),
  instagram: new Set([
    "impressions",
    "reach",
    "likes",
    "comments",
    "shares",
    "saves",
    "engagementRate",
  ]),
  // No platform-specific bundles: both fall back to the shared content copy.
  tiktok: new Set<string>(),
  snapchat: new Set<string>(),
};

const resolveKpiHelpBase = (leaf: string | undefined, platform?: OrganicPlatform) =>
  platform && leaf && PLATFORM_KPI_HELP_LEAVES[platform].has(leaf)
    ? `organicMedia.${platform}.kpiHelp`
    : "organicMedia.contentReport.kpiHelp";

/**
 * Post-table column help, addressed by its platform bundle leaf directly
 * (`impressions`, `reach`, `engagementRate`, …) rather than through an API
 * metric key. Falls back to the shared generic copy when a platform has no
 * entry for that leaf.
 */
export function getOrganicPlatformMetricDefinitionInfo(
  platform: OrganicPlatform,
  leaf: string,
  label: string,
  t: TFunction
): OrganicDefinitionInfo {
  const base = `organicMedia.${platform}.kpiHelp`;
  const description = t(`${base}.${leaf}.description`, { defaultValue: "" });
  if (description) {
    const formula = t(`${base}.${leaf}.formula`, { defaultValue: "" });
    return formula ? { description, formula } : { description };
  }

  const sharedBase = "organicMedia.contentReport.kpiHelp";
  const sharedDescription = t(`${sharedBase}.${leaf}.description`, {
    defaultValue: "",
  });
  if (sharedDescription) {
    const formula = t(`${sharedBase}.${leaf}.formula`, { defaultValue: "" });
    return formula
      ? { description: sharedDescription, formula }
      : { description: sharedDescription };
  }

  return {
    description: t(`${sharedBase}.fallback.description`, { label }),
    formula: t(`${sharedBase}.fallback.formula`, { label }),
  };
}

/** Resolves KPI help for Facebook/Instagram organic content reports. Tries `tooltipLabel` then `title` (API may localize titles). */
export function getOrganicContentKpiDefinitionInfo(
  title: string,
  tooltipLabel: string | undefined,
  formatKpiTitle: (raw: string, tfn: TFunction) => string,
  t: TFunction,
  platform?: OrganicPlatform
): OrganicDefinitionInfo {
  const leaf = resolveMapKey(title, tooltipLabel, CONTENT_KPI_KEY);
  const base = resolveKpiHelpBase(leaf, platform);
  if (leaf) {
    return readHelpBundle(t, base, leaf);
  }
  const label = formatKpiTitle(title, t);
  const fromTooltip = tooltipLabel?.trim();
  return {
    description:
      fromTooltip ||
      t(`${base}.fallback.description`, { label }),
    formula: t(`${base}.fallback.formula`, { label }),
  };
}

/** Section-level help (engagements, breakdowns, trends, post table). Keys map to `organicMedia.contentReport.sectionHelp.*`. */
export function getOrganicContentSectionDefinitionInfo(
  key: OrganicContentSectionKey,
  t: TFunction
): OrganicDefinitionInfo {
  return readHelpBundle(t, "organicMedia.contentReport.sectionHelp", key);
}

// Maps a total_engagements metricKey (snake_case, as returned by the API) to
// its `organicMedia.contentReport.kpiHelp` leaf (camelCase).
const ENGAGEMENT_METRIC_KEY: Record<string, string> = {
  // Facebook's total_engagements "viewers" row is labeled "Impressions" (see
  // organicMedia.facebook.engagementMetrics.viewers), so it shares that definition.
  viewers: "impressions",
  link_clicks: "linkClicks",
  shares: "shares",
  comments: "comments",
  reactions: "reactions",
  impressions: "impressions",
  likes: "likes",
  saves: "saves",
  profile_visits: "profileVisits",
  new_followers: "newFollowers",
};

/** Resolves per-card help for the Total Engagements grid (Viewers, Link Clicks, Shares, Comments, Reactions, etc.). */
export function getOrganicEngagementDefinitionInfo(
  metricKey: string,
  label: string,
  t: TFunction,
  platform?: OrganicPlatform
): OrganicDefinitionInfo {
  const leaf = ENGAGEMENT_METRIC_KEY[metricKey];
  const base = resolveKpiHelpBase(leaf, platform);
  if (leaf) {
    return readHelpBundle(t, base, leaf);
  }
  return {
    description: t(`${base}.fallback.description`, { label }),
    formula: t(`${base}.fallback.formula`, { label }),
  };
}

export function getFacebookFollowersDefinitionInfo(
  title: string,
  tooltipLabel: string | undefined,
  t: TFunction
): OrganicDefinitionInfo {
  const leaf = resolveMapKey(title, tooltipLabel, FB_FOLLOWERS_KPI_KEY);
  const base = "organicMedia.followersReport.facebook.kpiHelp";
  if (leaf) {
    return readHelpBundle(t, base, leaf);
  }
  const label = title;
  return {
    description: t("organicMedia.followersReport.fallback.description", { label }),
    formula: t("organicMedia.followersReport.fallback.formula", { label }),
  };
}

export function getInstagramFollowersDefinitionInfo(
  title: string,
  tooltipLabel: string | undefined,
  t: TFunction
): OrganicDefinitionInfo {
  const leaf = resolveMapKey(title, tooltipLabel, IG_FOLLOWERS_KPI_KEY);
  const base = "organicMedia.followersReport.instagram.kpiHelp";
  if (leaf) {
    return readHelpBundle(t, base, leaf);
  }
  const label = title;
  return {
    description: t("organicMedia.followersReport.fallback.description", { label }),
    formula: t("organicMedia.followersReport.fallback.formula", { label }),
  };
}

const ORGANIC_POST_TYPE_SLUGS = new Set([
  "photo",
  "video",
  "album",
  "link",
  "status",
  "reel",
  "story",
  "carousel",
  "carousel_album",
  "native_templates",
  "multi_share",
  "share",
  "note",
  "music",
  "map",
  "event",
  "live_video",
  "offer",
  "poll",
  "text",
  "spotlight",
  "image",
]);

const formatContentTypeFallback = (value: string) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());

/** Localized post / content type (Graph-style slugs); unknown values get a title-cased fallback. */
export function formatOrganicPostTypeLabel(raw: string, t: TFunction): string {
  const slug = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (slug && ORGANIC_POST_TYPE_SLUGS.has(slug)) {
    return t(`organicMedia.postTypes.${slug}`);
  }
  return formatContentTypeFallback(raw);
}
